import { createSlice, createSelector } from '@reduxjs/toolkit';

const initialState = {
  selectedComparables: [],
  status: 'idle'
};

const selectedComparablesSlice = createSlice({
  name: 'selectedComparables',
  initialState,
  reducers: {
    setSelectedComparables(state, action) {
      state.selectedComparables = action.payload;
      state.status = 'succeeded'; // Set status to succeeded when data is set
    },
    // Add/remove a single comparable, identified by _originalIndex -- assigned server-side per
    // transaction row and already what AG Grid uses as its getRowId, so grid and map agree on
    // identity without any extra mapping. Payload must be a transformed row (see
    // transformComparableRows): CreateValuationForm reads these rows by their camelCase field
    // names, so pushing a raw API row here would silently drop fields from saved valuations.
    toggleComparable(state, action) {
      const row = action.payload;
      const existingIndex = state.selectedComparables.findIndex((c) => c._originalIndex === row._originalIndex);

      if (existingIndex === -1) {
        state.selectedComparables.push(row);
      } else {
        state.selectedComparables.splice(existingIndex, 1);
      }
      state.status = 'succeeded';
    },
    startLoading(state) {
      state.status = 'loading'; // Set status to loading at the start of an operation
    },
    loadingFailed(state) {
      state.status = 'failed'; // Set status to failed when an operation fails
    },
    resetSelectedComparables: () => initialState // Added reset action
  }
});

export const { setSelectedComparables, toggleComparable, startLoading, loadingFailed, resetSelectedComparables } =
  selectedComparablesSlice.actions;

// Memoised: .map() builds a new array on every call, so an unmemoised selector handed a
// reference-equality check (useSelector's default, and useEffect dependency arrays) looks
// "changed" on every store update even when the selection is identical. That was making
// MapBoxPolygon's comparables effect re-run and call setData() on the comparables source,
// which re-tessellates the layer and visibly re-fades every polygon on the map.
export const selectSelectedParcelIds = createSelector([(state) => state.selectedComparables.selectedComparables], (comparables) =>
  comparables.map((comparable) => comparable.id)
);

// The set of selected _originalIndex values, for checked-state lookups (popup checkboxes, and
// the grid's sync-from-Redux). Memoised for the same reason as the selector above.
export const selectSelectedOriginalIndices = createSelector(
  [(state) => state.selectedComparables.selectedComparables],
  (comparables) => new Set(comparables.map((comparable) => comparable._originalIndex))
);

export default selectedComparablesSlice.reducer;
